import React, { useEffect, useMemo, useState } from "react";
import { marked } from "marked";
import { useTranslation } from "react-i18next";
import {
  fetchJsonWithFallback,
  fetchMarkdownWithFallback,
  getRegulationsIndexCandidates,
  normalizeLanguageCode,
} from "../utils/regulationsContent";
import "../styles/pages/FishingRegulations.css";

const CANONICAL_WATER_TYPE_LABELS = {
  Freshwater: ["Freshwater", "Сладководен риболов"],
  Saltwater: ["Saltwater", "Морски риболов"],
};

const CANONICAL_METHOD_LABELS = {
  Shore: ["Shore", "От брега"],
  Boat: ["Boat", "От лодка"],
};

const HIDDEN_REGIONS = ["Dubai"];

function normalizeCategoryValue(value, labelsByCanonicalValue) {
  const normalizedValue = String(value || "").trim().toLowerCase();
  if (!normalizedValue) {
    return "";
  }

  const match = Object.entries(labelsByCanonicalValue).find(([, labels]) =>
    labels.some((label) => String(label).trim().toLowerCase() === normalizedValue)
  );

  return match ? match[0] : String(value || "").trim();
}

function extractMdSection(md, waterType, method) {
  const canonicalWaterType = normalizeCategoryValue(
    waterType,
    CANONICAL_WATER_TYPE_LABELS
  );
  const canonicalMethod = normalizeCategoryValue(method, CANONICAL_METHOD_LABELS);

  const collectBlocks = (regex) => {
    const matches = [...md.matchAll(regex)];
    return matches.map((match) => match[1]).join("\n\n");
  };

  if (canonicalWaterType === "Freshwater") {
    const freshwater = collectBlocks(
      /<!-- FRESHWATER_START -->([\s\S]*?)<!-- FRESHWATER_END -->/g
    );
    return freshwater || md;
  }

  if (canonicalWaterType === "Saltwater") {
    if (canonicalMethod === "Shore") {
      const shore = collectBlocks(
        /<!-- SALTWATER_SHORE_START -->([\s\S]*?)<!-- SALTWATER_SHORE_END -->/g
      );
      return shore || md;
    }

    if (canonicalMethod === "Boat") {
      const boat = collectBlocks(
        /<!-- SALTWATER_BOAT_START -->([\s\S]*?)<!-- SALTWATER_BOAT_END -->/g
      );
      return boat || md;
    }

    const shore = collectBlocks(
      /<!-- SALTWATER_SHORE_START -->([\s\S]*?)<!-- SALTWATER_SHORE_END -->/g
    );
    const boat = collectBlocks(
      /<!-- SALTWATER_BOAT_START -->([\s\S]*?)<!-- SALTWATER_BOAT_END -->/g
    );
    const combined = [shore, boat].filter(Boolean).join("\n\n");
    return combined || md;
  }

  return md;
}

function getRegulationFilterLabel(option, t) {
  if (option === "All") {
    return t("all");
  }

  const canonicalWaterType = normalizeCategoryValue(
    option,
    CANONICAL_WATER_TYPE_LABELS
  );
  if (canonicalWaterType === "Saltwater") {
    return t("rules_saltwater");
  }
  if (canonicalWaterType === "Freshwater") {
    return t("rules_freshwater");
  }

  const canonicalMethod = normalizeCategoryValue(option, CANONICAL_METHOD_LABELS);
  if (canonicalMethod === "Shore") {
    return t("fishing_way_shore");
  }
  if (canonicalMethod === "Boat") {
    return t("fishing_way_boat");
  }

  return option;
}

export default function FishingRegulations() {
  const { t, i18n } = useTranslation();
  const [regulations, setRegulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState("All");
  const [waterType, setWaterType] = useState("All");
  const [method, setMethod] = useState("All");
  const [search, setSearch] = useState("");
  const [expandedMd, setExpandedMd] = useState({});
  const activeRegulationsLanguage = useMemo(
    () => normalizeLanguageCode(i18n.language),
    [i18n.language]
  );

  useEffect(() => {
    const controller = new AbortController();

    async function loadData() {
      setLoading(true);

      try {
        const json = await fetchJsonWithFallback(
          getRegulationsIndexCandidates(activeRegulationsLanguage),
          { signal: controller.signal }
        );

        if (!controller.signal.aborted) {
          setRegulations(Array.isArray(json) ? json : []);
          setExpandedMd({});
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Failed to load FishingRegulations index", err);
          if (!controller.signal.aborted) {
            setRegulations([]);
            setExpandedMd({});
          }
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => controller.abort();
  }, [activeRegulationsLanguage]);

  async function loadMarkdown(id, fileName, selectedWaterType, selectedMethod) {
    if (!fileName) return;

    try {
      const mdText = await fetchMarkdownWithFallback(
        fileName,
        activeRegulationsLanguage
      );
      const selected = extractMdSection(
        mdText,
        selectedWaterType,
        selectedMethod
      );

      setExpandedMd((prev) => ({
        ...prev,
        [`${id}_content`]: selected,
      }));
    } catch (err) {
      console.error("Failed to load markdown", fileName, err);
    }
  }

  const visibleRegulations = useMemo(
    () => regulations.filter((rule) => !HIDDEN_REGIONS.includes(rule.region)),
    [regulations]
  );

  const countries = useMemo(
    () => ["All", ...new Set(visibleRegulations.map((rule) => rule.country))],
    [visibleRegulations]
  );

  const waterTypes = useMemo(
    () => ["All", ...new Set(visibleRegulations.map((rule) => rule.waterType))],
    [visibleRegulations]
  );

  const methodOptions = useMemo(() => {
    if (
      normalizeCategoryValue(waterType, CANONICAL_WATER_TYPE_LABELS) !==
      "Saltwater"
    ) {
      return [];
    }

    const allMethods = new Set();
    visibleRegulations
      .filter(
        (rule) =>
          normalizeCategoryValue(rule.waterType, CANONICAL_WATER_TYPE_LABELS) ===
          "Saltwater"
      )
      .forEach((rule) => rule.fishingMethod?.forEach((item) => allMethods.add(item)));

    return ["All", ...Array.from(allMethods)];
  }, [visibleRegulations, waterType]);

  const filtered = useMemo(() => {
    return visibleRegulations.filter((item) => {
      if (country !== "All" && item.country !== country) return false;
      if (waterType !== "All" && item.waterType !== waterType) return false;

      if (
        normalizeCategoryValue(waterType, CANONICAL_WATER_TYPE_LABELS) ===
          "Saltwater" &&
        method !== "All"
      ) {
        if (!item.fishingMethod?.includes(method)) return false;
      }

      if (search.trim() !== "") {
        const normalizedSearch = search.toLowerCase();
        const haystack =
          `${item.areaName} ${item.country} ${item.keywords || ""}`.toLowerCase();
        if (!haystack.includes(normalizedSearch)) return false;
      }

      return true;
    });
  }, [visibleRegulations, country, waterType, method, search]);

  if (loading) {
    return <div className="local-regs-page">{t("regulations_loading")}</div>;
  }

  return (
    <div className="local-regs-page">
      <header className="local-regs-header">
        <h1 className="local-regs-title">{t("sidebar_regulations")}</h1>
        <p className="local-regs-subtitle">{t("regulations_subtitle")}</p>
      </header>

      <section className="local-regs-filters">
        <div className="local-regs-filter-group">
          <div className="local-regs-filter">
            <label>{t("country")}</label>
            <select value={country} onChange={(e) => setCountry(e.target.value)}>
              {countries.map((option) => (
                <option key={option} value={option}>
                  {getRegulationFilterLabel(option, t)}
                </option>
              ))}
            </select>
          </div>

          <div className="local-regs-filter">
            <label>{t("water_type")}</label>
            <select
              value={waterType}
              onChange={(e) => {
                setWaterType(e.target.value);
                setMethod("All");
              }}
            >
              {waterTypes.map((option) => (
                <option key={option} value={option}>
                  {getRegulationFilterLabel(option, t)}
                </option>
              ))}
            </select>
          </div>

          {normalizeCategoryValue(waterType, CANONICAL_WATER_TYPE_LABELS) ===
            "Saltwater" && (
            <div className="local-regs-filter">
              <label>{t("regulations_saltwater_type")}</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)}>
                {methodOptions.map((option) => (
                  <option key={option} value={option}>
                    {getRegulationFilterLabel(option, t)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="local-regs-filter local-regs-filter--search">
            <label>{t("trip_search")}</label>
            <input
              type="text"
              placeholder={t("regulations_search_placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            className="local-regs-clear-btn top-clear"
            onClick={() => {
              setCountry("All");
              setWaterType("All");
              setMethod("All");
              setSearch("");
            }}
          >
            {t("regulations_clear_filters")}
          </button>
        </div>
      </section>

      <section className="local-regs-summary">
        {t("regulations_showing")} <strong>{filtered.length}</strong>{" "}
        {filtered.length !== 1
          ? t("regulations_results_label")
          : t("regulations_result_label")}
      </section>

      <section className="local-regs-main">
        <div className="local-regs-list">
          {filtered.map((rule) => (
            <article key={rule.id} className="local-regs-card">
              <header className="local-regs-card-header">
                <h2 className="local-regs-card-title">{rule.areaName}</h2>
                <p className="local-regs-card-location">
                  {rule.country}{" \u2022 "}
                  {rule.waterType}
                </p>
              </header>

              <section className="local-regs-section">
                <h3>{t("regulations_licence_requirements")}</h3>

                {rule.licenseRequirements?.length > 0 ? (
                  <ul className="local-regs-licence-list">
                    {rule.licenseRequirements.map((lic, index) => (
                      <li key={index} className="local-regs-licence-item">
                        <p className="lic-summary">
                          <strong>{lic.licenseType}</strong>{" \u2014 "}
                          <span className="lic-mandatory">
                            {lic.mandatory || t("regulations_mandatory")}
                          </span>
                        </p>
                        <p>
                          <strong>{t("regulations_who")}:</strong> {lic.appliesTo}
                        </p>
                        <p>
                          <strong>{t("weather_label_water")}:</strong>{" "}
                          {lic.waterCoverage}
                        </p>
                        <p className="lic-important">*{lic.notes}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>{t("regulations_no_licence_info")}</p>
                )}
              </section>

              <section className="local-regs-section">
                <h3>{t("regulations_official_links")}</h3>
                {rule.externalLinks?.length ? (
                  <ul className="local-regs-links">
                    {rule.externalLinks.map((link, index) => (
                      <li key={index}>
                        <a href={link.url} target="_blank" rel="noreferrer">
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>{t("regulations_no_links_available")}</p>
                )}

                {rule.extendedInfoMd && (
                  <button
                    className="local-regs-more-btn"
                    onClick={() => {
                      const isOpen = expandedMd[rule.id] === "open";

                      if (!isOpen && !expandedMd[`${rule.id}_content`]) {
                        loadMarkdown(
                          rule.id,
                          rule.extendedInfoMd,
                          rule.waterType,
                          Array.isArray(rule.fishingMethod)
                            ? rule.fishingMethod[0] || "All"
                            : "All"
                        );
                      }

                      setExpandedMd((prev) => ({
                        ...prev,
                        [rule.id]: isOpen ? "" : "open",
                      }));
                    }}
                  >
                    {expandedMd[rule.id] === "open"
                      ? `${t("regulations_hide_details")} \u25B2`
                      : `${t("regulations_more_details")} \u25BC`}
                  </button>
                )}

                {expandedMd[rule.id] === "open" && (
                  <div className="local-regs-md-section">
                    <div
                      className="local-regs-md-content"
                      dangerouslySetInnerHTML={{
                        __html: expandedMd[`${rule.id}_content`]
                          ? marked.parse(expandedMd[`${rule.id}_content`])
                          : "",
                      }}
                    ></div>
                  </div>
                )}
              </section>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
